import {App as AntdApp, Alert, Divider, Tag} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetGaAuthUrlMutation,
	useGetGaAccountsQuery,
	useUseGaAccountMutation,
} from "~/reduxs/api/gaApiSlice";
// Tái dùng style panel kết nối của GSC (cùng bố cục, khỏi nhân đôi scss).
import style from "~/features/GscInsight/style/Gsc.module.scss";

const cn = className.bind(style);

/**
 * Panel kết nối Google Analytics 4 — mirror GscConnect. Credential dùng chung với GSC
 * (gsc_accounts): tài khoản đã kết nối từ GA4 (có scope analytics) dùng lại được ngay;
 * tài khoản kết nối từ GSC trước đây phải OAuth lại để cấp thêm quyền Analytics.
 */
function GaConnect({projectId, revoked = false, email}) {

	const {notification} = AntdApp.useApp();
	const [getAuthUrl, {isLoading}] = useGetGaAuthUrlMutation();
	const {data: accounts = []} = useGetGaAccountsQuery();
	const [applyAccount, {isLoading: using}] = useUseGaAccountMutation();

	// Dùng lại được: còn hiệu lực + token đã có scope analytics.
	const reusable = accounts.filter((a) => a.status !== 'revoked' && a.has_analytics);
	// Có kết nối GSC nhưng thiếu scope analytics → hiển thị để user hiểu vì sao phải OAuth lại.
	const needConsent = accounts.filter((a) => a.status !== 'revoked' && !a.has_analytics);

	const handleConnect = async () => {
		try {
			const res = await getAuthUrl(projectId).unwrap();
			if (!res?.url) throw new Error('Không lấy được URL kết nối');
			// state có hậu tố '.ga' — callback dùng chung /gsc/oauth/callback nhận diện tool.
			sessionStorage.setItem('ga_oauth_state', res.state || '');
			window.location.href = res.url;
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thể bắt đầu kết nối Google')});
		}
	};

	const handleUse = async (accountId) => {
		try {
			await applyAccount({projectId, account_id: accountId}).unwrap();
			notification.success({message: 'Đã dùng tài khoản', description: 'Hãy chọn property GA4 cho dự án này.'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không dùng được tài khoản này')});
		}
	};

	return (
		<div className={cn('connectPanel')}>
			<div className={cn('connectIcon')}>
				<FontAwesomeIcon icon="fa-brands fa-google" />
			</div>
			<div className={cn('connectTitle')}>
				{revoked ? 'Kết nối lại Google Analytics 4' : 'Kết nối Google Analytics 4'}
			</div>
			<div className={cn('connectDesc')}>
				Kết nối tài khoản Google có quyền xem property GA4 của website để đồng bộ lưu lượng thật
				(phiên, người dùng, tương tác, sự kiện chính, doanh thu). Chúng tôi chỉ xin quyền <strong>chỉ đọc</strong>.
			</div>

			{revoked && (
				<Alert
					type="warning"
					showIcon
					style={{textAlign: 'left', marginBottom: 18}}
					message="Kết nối trước đã bị thu hồi hoặc hết hạn"
					description={email ? `Tài khoản: ${email}. Vui lòng cấp quyền lại để tiếp tục đồng bộ.` : 'Vui lòng cấp quyền lại để tiếp tục đồng bộ.'}
				/>
			)}

			{/* Dùng lại tài khoản đã có scope Analytics (không cần OAuth lại). */}
			{!revoked && reusable.length > 0 && (
				<div style={{textAlign: 'left', marginBottom: 8}}>
					<div style={{fontSize: 13, color: '#6b7280', marginBottom: 8}}>
						Dùng lại tài khoản Google đã kết nối:
					</div>
					{reusable.map((a) => (
						<div key={a.id} style={{
							display: 'flex', alignItems: 'center', justifyContent: 'space-between',
							gap: 12, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8,
						}}>
							<span style={{display: 'flex', alignItems: 'center', gap: 8, minWidth: 0}}>
								<FontAwesomeIcon icon="fa-brands fa-google" />
								<span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{a.email || `Tài khoản #${a.id}`}</span>
							</span>
							<Button small primary loading={using} onClick={() => handleUse(a.id)}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-plug" />}>
								Dùng cho dự án này
							</Button>
						</div>
					))}
					<Divider plain style={{margin: '14px 0', color: '#9ca3af', fontSize: 12}}>hoặc</Divider>
				</div>
			)}

			{/* Tài khoản GSC chưa cấp quyền Analytics → giải thích vì sao phải kết nối lại. */}
			{!revoked && needConsent.length > 0 && (
				<div style={{textAlign: 'left', marginBottom: 12, fontSize: 13, color: '#6b7280'}}>
					{needConsent.map((a) => (
						<div key={a.id} style={{marginBottom: 4}}>
							<FontAwesomeIcon icon="fa-light fa-circle-info" /> {a.email}
							{' '}<Tag>chưa cấp quyền Analytics</Tag> — bấm nút bên dưới và chọn đúng tài khoản này để cấp thêm quyền.
						</div>
					))}
				</div>
			)}

			<Button primary loading={isLoading} onClick={handleConnect}
				leftIcon={<FontAwesomeIcon icon="fa-brands fa-google" />}>
				{revoked ? 'Kết nối lại' : (reusable.length > 0 ? 'Kết nối tài khoản Google khác' : 'Kết nối tài khoản Google')}
			</Button>
		</div>
	);
}

export default GaConnect;
